import { ROSTER, type CharacterId } from '../game/characters';
import { selectCharacter, unavailableCharacters } from '../game/player-select';
import { log } from '../util/log';
import { GAME_WIDTH, KoodooScene, TEXT_STYLES } from './base-scene';

/** Grid slots for the four racers, in roster order. */
export const PORTRAIT_SLOTS = [
  { x: 400, y: 240 },
  { x: 800, y: 240 },
  { x: 400, y: 500 },
  { x: 800, y: 500 },
] as const;

const PORTRAIT_Y_OFFSET = -10;
const NAME_Y_OFFSET = 64;
const TAKEN_ALPHA = 0.35;

export class PlayerSelect extends KoodooScene {
  static readonly KEY = 'PlayerSelect';

  private portraits = new Map<CharacterId, Phaser.GameObjects.Image>();
  private startRaceButton?: Phaser.GameObjects.Image;

  constructor() {
    super(PlayerSelect.KEY);
  }

  preload(): void {
    this.loadBackgrounds();
    this.load.image('back_button', 'assets/images/ui-objects/back-icon.png');
    this.load.image('start_race', 'assets/images/ui-objects/start-race-icon.png');
    this.load.image('star_1x1', 'assets/images/ui-objects/star-1x1.png');
    this.load.image(
      'player_select_logo',
      'assets/images/ui-objects/player-select-logo.png',
    );
    for (const character of ROSTER) {
      this.load.image(character.id, `assets/images/players/${character.id}-1x1.png`);
    }
  }

  create(): void {
    this.playMenuTheme();
    // Phaser reuses scene instances across `scene.start`, so these would
    // otherwise still hold game objects destroyed with the previous visit.
    this.portraits.clear();
    this.startRaceButton = undefined;

    this.addBackgrounds();
    this.add.image(GAME_WIDTH / 2, 70, 'player_select_logo');

    this.addBackButton();
    this.addStartRaceButton();

    for (const [index, character] of ROSTER.entries()) {
      this.addPortrait(character.id, character.displayName, PORTRAIT_SLOTS[index]!);
    }

    this.refresh();
  }

  /**
   * Pushes the pure selection state onto the screen: whose cursor is live,
   * which racers are greyed out, and whether the flag button is available.
   */
  private refresh(): void {
    const state = this.session.getSelectState();
    this.setCursor(state.cursor);

    const taken = unavailableCharacters(state);
    for (const [id, portrait] of this.portraits) {
      portrait.setAlpha(taken.includes(id) ? TAKEN_ALPHA : 1);
    }

    // `setInteractive` takes a hit area, not an enabled flag — passing a
    // boolean gives Phaser a truthy hit area with no callback, which throws on
    // the next pointer event and soft-locks the screen.
    if (state.readyToRace) {
      this.startRaceButton?.setAlpha(0.8).setInteractive();
    } else {
      this.startRaceButton?.setAlpha(0).disableInteractive();
    }
  }

  private onPortraitClicked(id: CharacterId): void {
    const state = this.session.getSelectState();
    log(`${state.cursor} wants ${id}`, 'cursor');

    const outcome = selectCharacter(state, id);
    if (!outcome.ok) {
      log(`refused ${id}: ${outcome.reason}`, 'debug');
      return;
    }
    this.session.setSelectState(outcome.state);
    this.session.audio.playVoiceLine(id, 'select');
    this.refresh();
  }

  private addPortrait(
    id: CharacterId,
    displayName: string,
    slot: { x: number; y: number },
  ): void {
    this.add.image(slot.x, slot.y, 'star_1x1');

    const portrait = this.add
      .image(slot.x, slot.y + PORTRAIT_Y_OFFSET, id)
      .setInteractive();
    this.portraits.set(id, portrait);

    this.add
      .text(slot.x, slot.y + NAME_Y_OFFSET, displayName, TEXT_STYLES.characterName)
      .setOrigin(0.5);

    portrait.on('pointerover', () => {
      if (this.isPickable(id)) {
        portrait.setScale(1.05);
        this.session.audio.playVoiceLine(id, 'hover');
      }
    });
    portrait.on('pointerout', () => portrait.setScale(1));
    portrait.on('pointerup', () => {
      if (this.isPickable(id)) {
        portrait.setScale(0.95);
        this.onPortraitClicked(id);
      }
    });
  }

  private isPickable(id: CharacterId): boolean {
    const state = this.session.getSelectState();
    return state.cursor !== 'generic' && !unavailableCharacters(state).includes(id);
  }

  private addBackButton(): void {
    const back = this.add.image(80, 640, 'back_button').setInteractive();
    back.setAlpha(0.8);
    back.on('pointerover', () => {
      back.setAlpha(1);
      this.session.audio.playEffect('mouse_over');
    });
    back.on('pointerout', () => back.setAlpha(0.8));
    back.on('pointerup', () => {
      this.session.audio.playEffect('mouse_click');
      this.scene.start('MainMenu');
    });
  }

  private addStartRaceButton(): void {
    const start = this.add.image(1180, 620, 'start_race');
    start.setAlpha(0);
    start.on('pointerover', () => {
      start.setAlpha(1);
      this.session.audio.playEffect('mouse_over');
    });
    start.on('pointerout', () => start.setAlpha(0.8));
    start.on('pointerup', () => {
      this.session.audio.playEffect('mouse_click');
      this.scene.start('Race');
    });
    this.startRaceButton = start;
  }
}
